/**
 * LicenseRequiredGate - ecran plein bloque + CTA upgrade. v2026.06.96.
 * Supporte requires='license'|'pro'|'business' avec badge + sous-titre adaptes.
 */
import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Card, Title2, Body1, Button, Caption1, tokens } from '@fluentui/react-components';
import { LockClosed24Filled, Sparkle20Filled } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useLicense } from '../auth/LicenseGate';

type RequiredPlan = 'license' | 'pro' | 'business';

interface Props {
  children:        ReactNode;
  featureNameKey?: string;
  requires?:       RequiredPlan;
  currentPlan?:    string;
}

const BADGE_KEY: Record<RequiredPlan, string> = {
  license:  'licenseGate.badge',
  pro:      'licenseGate.badgePro',
  business: 'licenseGate.badgeBusiness',
};

const SUBTITLE_KEY: Record<RequiredPlan, string> = {
  license:  'licenseGate.subtitle',
  pro:      'licenseGate.subtitlePro',
  business: 'licenseGate.subtitleBusiness',
};

const PRO_OK = new Set(['pro', 'business', 'enterprise']);
const BIZ_OK = new Set(['business', 'enterprise']);

export function LicenseRequiredGate({
  children,
  featureNameKey,
  requires = 'license',
  currentPlan,
}: Props) {
  const { t } = useTranslation();
  const license = useLicense();

  if (license.loading) return null;

  // Mode standalone : children fournis -> on calcule allowed, sinon on bloque toujours.
  if (children !== null && children !== undefined) {
    const plan  = (license.status?.plan ?? '').toLowerCase();
    const valid = Boolean(license.hasLicense && license.status?.valid);
    let isAllowed = false;
    if (requires === 'license')       isAllowed = !license.isTrial;
    else if (requires === 'pro')      isAllowed = valid && PRO_OK.has(plan);
    else if (requires === 'business') isAllowed = valid && BIZ_OK.has(plan);
    if (isAllowed) return <>{children}</>;
  }

  const featureName = featureNameKey ? t(featureNameKey) : t('licenseGate.thisFeature');
  const planLabel   = currentPlan ?? (license.status?.plan ?? '').toLowerCase();

  return (
    <div style={{ maxWidth: 720, margin: '60px auto', padding: '0 20px' }}>
      <Card style={{
        padding: 48,
        textAlign: 'center',
        background: 'linear-gradient(135deg, #FFF8E6 0%, #FEF3C7 100%)',
        border: `1px solid ${tokens.colorPaletteYellowBorder1}`,
      }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '6px 14px',
          background: 'linear-gradient(135deg, #D9842B, #B45309)',
          color: '#fff', borderRadius: 999,
          fontSize: 11, fontWeight: 700,
          letterSpacing: '0.08em', textTransform: 'uppercase',
          marginBottom: 24,
        }}>
          <Sparkle20Filled style={{ width: 14, height: 14 }} />
          {t(BADGE_KEY[requires])}
        </div>

        <div style={{
          width: 72, height: 72, borderRadius: '50%',
          background: 'rgba(180,83,9,0.12)',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: 20,
        }}>
          <LockClosed24Filled style={{ width: 36, height: 36 }} primaryFill="#B45309" />
        </div>

        <Title2 block style={{ marginBottom: 12, color: '#1F3D5C' }}>
          {t('licenseGate.title', { feature: featureName })}
        </Title2>

        <Body1 block style={{
          color: tokens.colorNeutralForeground2,
          marginBottom: 16,
          maxWidth: 520, marginLeft: 'auto', marginRight: 'auto',
        }}>
          {t(SUBTITLE_KEY[requires])}
        </Body1>

        {planLabel && (
          <Caption1 block style={{
            color: tokens.colorNeutralForeground3,
            marginBottom: 28,
            fontStyle: 'italic',
          }}>
            {t('licenseGate.currentPlan', { plan: planLabel })}
          </Caption1>
        )}

        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link to="/billing" style={{ textDecoration: 'none' }}>
            <Button appearance="primary" size="large">
              {t('licenseGate.upgradeCta')} →
            </Button>
          </Link>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <Button appearance="subtle" size="large">
              {t('licenseGate.backToDashboard')}
            </Button>
          </Link>
        </div>
      </Card>
    </div>
  );
}
